/**
 * 「协作管理」的中文文案**唯一来源**（L 域 · `AC-L1.x` / `L5.x` / `L6.x`）
 *
 * 依据：L 域 PRD **§5.1**（邀请与角色）· **§5.5**（加入家庭）· **§5.6**（移除 / 解散）·
 * `DR-018`（协作域口径定稿）· 原型 `08-家庭与设置_原型.html` 面板 1「成员与贡献」。
 *
 * ## 🔴 本文件只写"服务端不下发的中文"
 *
 * | 不写在这里 | 为什么 |
 * |---|---|
 * | 角色名（所有者 / 成员） | 由 `features/collab.ts` 的 `memberRoleText()` 按 `LedgerRole` 派生（枚举单源） |
 * | 错误文案（409 满员 / 400 名称不符 / 403 无权限） | 🔴 **原样展示服务端 `message`**（铁律 79：服务端定下的规则不在端侧抄第二遍；猜文案会在服务端改措辞时静默撒谎） |
 * | 拆分报告里的数量 | 由 `movedCountsText()` 按服务端 `MovedCounts` 拼（数字一个都不在端侧算） |
 *
 * ## ⚠️ 两处措辞的取舍
 *
 * - **「移除」不叫「删除成员」**：`DR-018` §5.6 的语义是**一次单人拆分**（这个人带着自己的
 *   账户与流水去一本新账本），数据一条都不丢 —— 叫"删除"会让用户以为对方的数据被抹了。
 * - **「解散」必须说"分家不是删除"**：`AC-L5.17` 明确不设冷静期，理由正是
 *   「拆分是分家不是删除（数据没丢）」；不写这句，用户会以为点下去全家数据归零。
 */

// ─────────────────────────── 邀请码（§5.1） ───────────────────────────

export const INVITE_SECTION_TITLE = '邀请家人';

/** 🔴 单码模型的代价必须说清：重新生成 = 旧码**立刻**失效（`AC-L1.5`） */
export const INVITE_ROTATE_HINT = '重新生成后，之前发出去的邀请码立即失效。';

export const INVITE_EMPTY_HINT = '还没有邀请码。生成一个发给家人，对方在「加入家庭」里输入即可。';

/** 邀请码只在管理界面露出（`@RequireOwner`）—— 这句解释"为什么别人看不到" */
export const INVITE_OWNER_ONLY_HINT = '只有账本所有者能生成邀请码。';

export const INVITE_COPY_DONE = '已复制';

// ─────────────────────────── 成员 / 角色（§5.1-2） ───────────────────────────

export const MEMBERS_SECTION_TITLE = '成员';

export const MEMBERS_SHARED_HINT = '成员名单对所有成员完全一致 —— 加入后就能看到全家的账，不需要谁给你开权限。';

/** 成员上限的**展示**用（4 人）—— 但"满了没满"由服务端 409 判定，端侧不自己数 */
export const MEMBERS_CAP_HINT = '家庭账本最多 4 位成员。';

// ─────────────────────────── 移除成员（§5.6） ───────────────────────────

export const REMOVE_TITLE = '移除成员';

export function removeExplain(name: string): string {
  return `会为「${name}」单独建一本账本，把他/她记的账户与流水原样搬过去 —— 数据一条都不会丢。`;
}

export const REMOVE_CONFIRM_LABEL = '再次输入这位成员的姓名';

// ─────────────────────────── 转让所有权（§5.1-2） ───────────────────────────

export const TRANSFER_TITLE = '转让所有权';

export function transferExplain(name: string): string {
  return `转让后「${name}」成为账本所有者（可以邀请、移除成员、解散账本），你变成普通成员。`;
}

// ─────────────────────────── 解散账本（§5.6） ───────────────────────────

export const DISSOLVE_TITLE = '解散账本';

export const DISSOLVE_EXPLAIN =
  '解散 = 分家，不是删除：每位成员都会拿到一本自己的账本，各账户与流水按归属搬过去，数据不会消失。';

export const DISSOLVE_CONFIRM_LABEL = '再次输入账本名称';

// ─────────────────────────── 加入家庭（§5.5） ───────────────────────────

export const JOIN_SECTION_TITLE = '加入家庭';

export const JOIN_SECTION_HINT = '有家人给了你邀请码？在这里输入就能加入同一个账本。';

export const JOIN_CODE_LABEL = '邀请码';

export const JOIN_PREVIEW_TITLE = '加入前先看清会发生什么';

/** 4 栏标题（§5.5.2） */
export const JOIN_PREVIEW_MERGE_TITLE = '① 将并入的数量';
export const JOIN_PREVIEW_RENAME_TITLE = '② 撞名怎么处理';
export const JOIN_PREVIEW_DISCARD_TITLE = '③ 不并入的内容';
export const JOIN_PREVIEW_WARNING_TITLE = '④ 不可撤销提醒';

export const JOIN_STRATEGY_TITLE = '你现在的个人账本怎么办？';

export const JOIN_STRATEGY_MERGE = '并入家庭账本（推荐）';
export const JOIN_STRATEGY_MERGE_HINT = '账户会新建行、绝不合并余额；原账本软隐藏，数据保留。';

export const JOIN_STRATEGY_DISCARD = '不并入，直接舍弃';
export const JOIN_STRATEGY_DISCARD_HINT = '原账本的数据会被硬删，不可恢复。';

/** `willMerge === null`（无源可并）⇒ **没有**策略可选，只能直接加入 */
export const JOIN_NO_SOURCE_HINT = '你还没有自己的账本，所以直接加入即可，没有需要并入的内容。';

export const JOIN_DONE_TITLE = '已加入';

export function joinDoneExplain(name: string): string {
  return `你现在是「${name}」的成员，账本上下文已切换过去。`;
}

/** 丢弃清单的三种性质（契约 `JoinPreviewDiscard.disposition`）—— 🔴 三种要分开说，不能合并成"丢弃" */
export const DISCARD_DISPOSITION_TEXT: Readonly<Record<string, string>> = {
  discarded: '丢弃（账本合并时明列不并入）',
  unaffected: '不受影响（属用户级设置，不在账本里）',
  deferred: '留在原账本（尚未支持并入）',
};
